import { LINK_TYPES, type LinkType } from "@gjwtrack/domain";
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, Copy, PanelLeft } from "lucide-react";
import { useSyncExternalStore, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { getListContext, subscribeListContext } from "../lib/listContext.ts";
import type { LinkTo } from "./NewIssueDialog.tsx";
import { Menu } from "../ui/Menu.tsx";
import { Button, ButtonGroup } from "../ui/ring.ts";
import { SearchBox } from "./SearchBox.tsx";
import { COPY_SHORTCUT_LABEL, copyIdAndSummary } from "../lib/copyIssue.ts";

export interface Crumb {
  label: string;
  to?: string;
  strike?: boolean;
  /** Issue crumb: the copy icon puts "KEY summary" on the clipboard (YouTrack's "Copy ID and summary"). */
  copy?: { key: string; summary: string; url: string };
  /** Issue keys are monospace; page titles are bold sans. */
  mono?: boolean;
}

interface Props {
  crumbs: Crumb[];
  /** Current query; the search box shows it as a pill with the rest editable. Omit both to leave the search box out (the Issues page has its own). */
  query?: string;
  onQuery?: (q: string) => void;
  /** Issue page: YouTrack's ← before the breadcrumbs, back to the list. */
  back?: string;
  /** Rendered right after the breadcrumbs (the Issues page puts its view menu and the issue count there). */
  after?: ReactNode;
  /** Detail page: id of the shown issue for the "n of N" pager. */
  currentIssueId?: number;
  /** Open the New issue dialog; on a detail page the caret menu asks for a link to the current issue. */
  onCreate: (opts?: { linkTo: LinkTo }) => void;
  onTogglePanel?: () => void;
  right?: ReactNode;
}

/** Breadcrumbs · search · pager · New issue — the row YouTrack puts above every page. */
export function TopBar({ crumbs, query, onQuery, back, after, currentIssueId, onCreate, onTogglePanel, right }: Props) {
  // YouTrack's order: relates to, is required for, depends on, is duplicated by, duplicates, parent for, subtask of.
  const linkChoices = (Object.keys(LINK_TYPES) as LinkType[]).flatMap((type) => {
    const def = LINK_TYPES[type];
    return def.symmetric ? [{ type, outward: true, label: def.outward }] : [{ type, outward: false, label: def.inward }, { type, outward: true, label: def.outward }];
  });
  const navigate = useNavigate();
  const list = useSyncExternalStore(subscribeListContext, getListContext);
  const idx = currentIssueId === undefined ? -1 : list.ids.indexOf(currentIssueId);

  const step = (d: number): void => {
    const next = list.ids[idx + d];
    if (next !== undefined) navigate(`/issue/${keyOf(next)}`);
  };
  const keyOf = (id: number): string => document.querySelector<HTMLElement>(`[data-issue-id="${id}"]`)?.dataset.issueKey ?? String(id);

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-ground px-3">
      {onTogglePanel && (
        <button className="icon-btn" onClick={onTogglePanel} title="Toggle panel">
          <PanelLeft size={18} />
        </button>
      )}
      {back && (
        <Link to={back} className="icon-btn" title="Back">
          <ArrowLeft size={18} />
        </Link>
      )}
      <nav className="flex min-w-0 items-center gap-1.5 text-[15px]" aria-label="Breadcrumb">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-faint">/</span>}
            {c.to ? (
              <Link to={c.to} className={`text-muted hover:text-ink ${c.strike ? "line-through" : ""}`}>
                {c.label}
              </Link>
            ) : (
              <span className={`${c.mono ? "font-mono" : "text-base font-semibold"} ${c.strike ? "text-muted line-through" : ""}`}>{c.label}</span>
            )}
            {c.copy && (
              <button className="icon-btn h-6 w-6 text-accent" title={`Copy ID and summary to clipboard  ${COPY_SHORTCUT_LABEL}`} onClick={() => void copyIdAndSummary(c.copy!.key, c.copy!.summary, c.copy!.url)}>
                <Copy size={15} />
              </button>
            )}
          </span>
        ))}
      </nav>
      {after}
      <div className="flex-1" />
      {query !== undefined && onQuery && <SearchBox query={query} onQuery={onQuery} className="w-[28rem] max-w-[40vw]" />}
      {currentIssueId !== undefined && idx >= 0 && (
        <span className="flex items-center gap-1 text-sm text-muted tabular-nums">
          <button className="icon-btn" disabled={idx <= 0} onClick={() => step(-1)} title="Previous issue">
            <ChevronLeft size={18} />
          </button>
          {idx + 1} of {list.total}
          <button className="icon-btn" disabled={idx >= list.ids.length - 1} onClick={() => step(1)} title="Next issue">
            <ChevronRight size={18} />
          </button>
        </span>
      )}
      {right}
      {/* Split primary button. Ring makes an icon-only Button inline (transparent) unless told otherwise, and paints the
          group frame in the grey border colour; .primary-group recolours it so the seam is the only visible line. */}
      <ButtonGroup className="primary-group">
        <Button primary onClick={() => onCreate()}>
          New issue
        </Button>
        {currentIssueId === undefined ? (
          <Button primary inline={false} icon={ChevronDown} title="New issue" onClick={() => onCreate()} />
        ) : (
          <Menu
            anchor={<Button primary inline={false} icon={ChevronDown} title="New issue linked to this one" />}
            title="Will be linked to current as:"
            items={linkChoices.map((c) => ({ key: c.label, label: c.label, onClick: () => onCreate({ linkTo: { issueId: currentIssueId, type: c.type, outward: c.outward } }) }))}
          />
        )}
      </ButtonGroup>
    </header>
  );
}
